/**
 * API wrapper for the Aufträge module (Phase 5 Stufe I, Slice 1, 2026-05-02).
 * Maps onto the backend routes under /api/v1/auftraege and the related
 * sub-resources (Berichte, AB-Download). Strictly typed so the routes can
 * call methods without ad-hoc fetch calls.
 */
import { api } from './api';

export type AuftragTyp =
  | 'reparatur'
  | 'wartung'
  | 'notdienst'
  | 'kontrollgang'
  | 'serienleistung'
  | 'beratung'
  | 'reinigung'
  | 'garten'
  | 'reklamation'
  | 'sonstige';

type AuftragQuelle =
  | 'disponiert'
  | 'selbst_erfasst'
  | 'serienleistung'
  | 'folgeauftrag'
  | 'kunden_anfrage';

export type AuftragStatus =
  | 'entwurf'
  | 'bestaetigt'
  | 'geplant'
  | 'in_arbeit'
  | 'abgeschlossen'
  | 'abgerechnet'
  | 'storniert'
  | 'abgebrochen';

export const MA_KLASSIFIZIERUNGEN = ['kontrollgang', 'auftrag', 'zusatzaufwand'] as const;
export type MaKlassifizierung = (typeof MA_KLASSIFIZIERUNGEN)[number];

export const BUERO_ENTSCHEIDUNGEN = [
  'pending',
  'abrechnen',
  'bv_pauschale',
  'garantie_kulanz',
] as const;
export type BueroEntscheidung = (typeof BUERO_ENTSCHEIDUNGEN)[number];

export const BUERO_ENTSCHEIDUNGEN_AKTIV = ['abrechnen', 'bv_pauschale', 'garantie_kulanz'] as const;
export type BueroEntscheidungAktiv = (typeof BUERO_ENTSCHEIDUNGEN_AKTIV)[number];

export interface Auftrag {
  id: string;
  kundeId: string;
  objektId: string | null;
  subunternehmerId: string | null;
  assignedTo: string | null;
  titel: string;
  beschreibung: string | null;
  typ: AuftragTyp;
  quelle: AuftragQuelle;
  status: AuftragStatus;
  projektId: string | null;
  scheduledAt: string | null;
  gewaehrleistungBis: string | null;
  mangelKategorie: string | null;
  maKlassifizierung: MaKlassifizierung | null;
  bueroEntscheidung: BueroEntscheidung | null;
  entschiedenAm: string | null;
  entschiedenDurchUserId: string | null;
  zusatzaufwandZuAuftragId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KlassifiziereDurchMaInput {
  klassifizierung: MaKlassifizierung;
  zusatzaufwandZuAuftragId?: string | null;
}

export interface EntscheideDurchBueroInput {
  entscheidung: BueroEntscheidungAktiv;
}

interface CreateAuftragInput {
  kundeId: string;
  objektId?: string;
  subunternehmerId?: string;
  titel: string;
  beschreibung?: string;
  typ?: AuftragTyp;
  quelle?: AuftragQuelle;
  assignedTo?: string;
  scheduledAt?: string;
}

interface UpdateAuftragInput {
  titel?: string;
  beschreibung?: string;
  status?: AuftragStatus;
  assignedTo?: string;
  scheduledAt?: string;
}

interface AuftragsbestaetigungBrief {
  id: string;
  auftragId: string;
  nummer: string;
  versendetAm: string | null;
  pdfStorageKey: string | null;
}

interface BerichtFotoMeta {
  id: string;
  contentType: string;
  sizeBytes: number;
  originalFilename: string;
}

interface BerichtMaterial {
  id: string;
  bezeichnung: string;
  menge: string;
  einheit: string | null;
}

export interface Bericht {
  id: string;
  auftragId: string;
  text: string;
  erstelltAm: string;
  erstelltVon: string | null;
  fotos: BerichtFotoMeta[];
  material: BerichtMaterial[];
}

export interface CreateBerichtInput {
  text: string;
  material?: Array<{ bezeichnung: string; menge: string; einheit?: string }>;
}

export const AUFTRAG_TYP_LABEL: Record<AuftragTyp, string> = {
  reparatur: 'Reparatur',
  wartung: 'Wartung',
  notdienst: 'Notdienst',
  kontrollgang: 'Kontrollgang',
  serienleistung: 'Serienleistung',
  beratung: 'Beratung',
  reinigung: 'Reinigung',
  garten: 'Garten',
  reklamation: 'Reklamation',
  sonstige: 'Sonstige',
};

export const AUFTRAG_STATUS_LABEL: Record<AuftragStatus, string> = {
  entwurf: 'Entwurf',
  bestaetigt: 'Bestätigt',
  geplant: 'Geplant',
  in_arbeit: 'In Arbeit',
  abgeschlossen: 'Abgeschlossen',
  abgerechnet: 'Abgerechnet',
  storniert: 'Storniert',
  abgebrochen: 'Abgebrochen',
};

export const AUFTRAG_STATUS_BADGE: Record<AuftragStatus, string> = {
  entwurf: 'bg-slate-100 text-slate-700 border-slate-200',
  bestaetigt: 'bg-blue-100 text-blue-800 border-blue-200',
  geplant: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  in_arbeit: 'bg-amber-100 text-amber-800 border-amber-200',
  abgeschlossen: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  abgerechnet: 'bg-emerald-200 text-emerald-900 border-emerald-300',
  storniert: 'bg-rose-100 text-rose-800 border-rose-200',
  abgebrochen: 'bg-rose-100 text-rose-800 border-rose-200',
};

export const MA_KLASSIFIZIERUNG_LABEL: Record<MaKlassifizierung, string> = {
  kontrollgang: 'Kontrollgang',
  auftrag: 'Auftrag',
  zusatzaufwand: 'Zusatzaufwand',
};

export const BUERO_ENTSCHEIDUNG_LABEL: Record<BueroEntscheidung, string> = {
  pending: 'Offen',
  abrechnen: 'Abrechnen',
  bv_pauschale: 'BV-Pauschale',
  garantie_kulanz: 'Garantie / Kulanz',
};

export const BUERO_ENTSCHEIDUNG_BADGE: Record<BueroEntscheidung, string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  abrechnen: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  bv_pauschale: 'bg-sky-100 text-sky-800 border-sky-200',
  garantie_kulanz: 'bg-violet-100 text-violet-800 border-violet-200',
};

/**
 * Photo-upload uses multipart/form-data instead of the JSON `api()`
 * helper. The backend route reads the CSRF cookie just like the
 * JSON path, so we replicate that header here. Errors are normalised
 * onto the same `ApiError` shape so route handlers don't need a
 * second error path.
 */
async function uploadMultipart<T>(path: string, formData: FormData): Promise<T> {
  const csrfMatch = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('busch_erp_csrf='));
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (csrfMatch) headers['X-CSRF-Token'] = decodeURIComponent(csrfMatch.split('=')[1] ?? '');

  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: formData,
  });

  const contentType = res.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } })?.error;
    const e = new Error(err?.message ?? res.statusText) as Error & {
      status?: number;
      code?: string;
    };
    e.status = res.status;
    e.code = err?.code ?? 'HTTP_' + res.status;
    throw e;
  }
  return payload as T;
}

export const auftraegeApi = {
  async list(): Promise<{ items: Auftrag[] }> {
    return api<{ items: Auftrag[] }>('/api/v1/auftraege');
  },

  async getById(id: string): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}`);
  },

  async create(input: CreateAuftragInput): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>('/api/v1/auftraege', { method: 'POST', body: input });
  },

  async update(id: string, input: UpdateAuftragInput): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}`, { method: 'PATCH', body: input });
  },

  async setStatus(id: string, status: AuftragStatus): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}`, {
      method: 'PATCH',
      body: { status },
    });
  },

  /**
   * FX-003 / P-003: Storno mit Pflicht-Grund. Pfad ergibt sich aus Status
   * (ADR 0010 #4): 'storniert' für entwurf|bestaetigt|geplant,
   * 'abgebrochen' für in_arbeit. Server lehnt falsche Kombination mit 409 ab.
   */
  async stornieren(
    id: string,
    body: { grund: string; pfad: 'storniert' | 'abgebrochen' },
  ): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}/stornieren`, {
      method: 'POST',
      body,
    });
  },

  async confirm(
    id: string,
  ): Promise<{ auftrag: Auftrag; bestaetigung: AuftragsbestaetigungBrief }> {
    return api<{ auftrag: Auftrag; bestaetigung: AuftragsbestaetigungBrief }>(
      `/api/v1/auftraege/${id}/bestaetigen`,
      { method: 'POST', body: {} },
    );
  },

  abPdfUrl(auftragId: string): string {
    return `/api/v1/auftraege/${auftragId}/bestaetigung/pdf`;
  },

  async listOffeneEntscheidungen(): Promise<{ items: Auftrag[] }> {
    return api<{ items: Auftrag[] }>('/api/v1/auftraege/offene-entscheidungen');
  },

  async klassifizieren(id: string, input: KlassifiziereDurchMaInput): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}/klassifizieren`, {
      method: 'POST',
      body: input,
    });
  },

  async entscheiden(id: string, input: EntscheideDurchBueroInput): Promise<{ item: Auftrag }> {
    return api<{ item: Auftrag }>(`/api/v1/auftraege/${id}/entscheiden`, {
      method: 'POST',
      body: input,
    });
  },

  async listBerichte(auftragId: string): Promise<{ items: Bericht[] }> {
    return api<{ items: Bericht[] }>(`/api/v1/auftraege/${auftragId}/berichte`);
  },

  async createBericht(auftragId: string, input: CreateBerichtInput): Promise<{ item: Bericht }> {
    return api<{ item: Bericht }>(`/api/v1/auftraege/${auftragId}/berichte`, {
      method: 'POST',
      body: input,
    });
  },

  async uploadBerichtFoto(
    auftragId: string,
    berichtId: string,
    file: File,
  ): Promise<{ item: BerichtFotoMeta }> {
    const fd = new FormData();
    fd.append('foto', file, file.name);
    return uploadMultipart<{ item: BerichtFotoMeta }>(
      `/api/v1/auftraege/${auftragId}/berichte/${berichtId}/fotos`,
      fd,
    );
  },

  berichtFotoUrl(auftragId: string, berichtId: string, fotoId: string): string {
    return `/api/v1/auftraege/${auftragId}/berichte/${berichtId}/fotos/${fotoId}`;
  },
};
